import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Scan, Plus, Minus, Trash2, Search, ArrowLeft, User, UserPlus,
  Printer, Banknote, Hourglass, Sparkles, X, Crown, AlertCircle, PackagePlus,
  FileSignature, Check,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import BarcodeScanner from '../../components/BarcodeScanner';
import { extractErrorMessage } from '../../utils/errorHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOLD = 'var(--color-primary)';

const TIER_LABELS = {
  detail: 'Détail',
  semi_gros: 'Semi-gros',
  gros: 'Gros',
  vip: 'VIP',
};

const TIER_COLORS = {
  detail: 'text-zinc-300 border-zinc-600',
  semi_gros: 'text-blue-300 border-blue-500/60',
  gros: 'text-emerald-300 border-emerald-500/60',
  vip: 'text-[color:var(--color-primary)] border-[color:var(--color-primary)]',
};

// ---- Pricing logic (mirrors backend & cart) -----------------------------

const computeCartTier = (totalQty) => {
  if (totalQty >= 60) return 'gros';
  if (totalQty < 12) return 'detail';
  return 'mixed';
};

const computeLine = (product, quantity, override, cartTier) => {
  // Returns { tier, unit_price, total }
  if (!product || quantity <= 0) return { tier: 'detail', unit_price: 0, total: 0 };
  const o = (override || '').toLowerCase();
  if (o === 'vip') {
    const cp = Number(product.cost_price || 0);
    if (cp > 0) {
      const margin = product.vip_margin == null || product.vip_margin === ''
        ? 1
        : Number(product.vip_margin);
      const safeMargin = Number.isFinite(margin) && margin >= 0 ? margin : 1;
      const u = Math.round((cp + safeMargin) * 100) / 100;
      return { tier: 'vip', unit_price: u, total: Math.round(u * quantity * 100) / 100 };
    }
    // No cost_price → fallback gros (same as backend)
    const u = Number(product.price_gros || 0);
    return { tier: 'gros', unit_price: u, total: Math.round(u * quantity * 100) / 100 };
  }
  if (['detail', 'semi_gros', 'gros'].includes(o)) {
    const u = Number(product[`price_${o}`] || 0);
    return { tier: o, unit_price: u, total: Math.round(u * quantity * 100) / 100 };
  }
  // Auto
  let tier;
  if (cartTier === 'gros') tier = 'gros';
  else if (cartTier === 'detail') tier = 'detail';
  else tier = quantity >= 12 ? 'gros' : 'semi_gros';
  const u = Number(product[`price_${tier}`] || 0);
  return { tier, unit_price: u, total: Math.round(u * quantity * 100) / 100 };
};

// =========================================================================

const AdminPreparation = () => {
  const { isStaff } = useAuth();
  const navigate = useNavigate();

  // Customer state
  const [mode, setMode] = useState('walkin'); // 'walkin' | 'existing'
  const [walkinName, setWalkinName] = useState('');
  const [walkinPhone, setWalkinPhone] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Cart state — items: [{ product, quantity, override? }]
  const [items, setItems] = useState([]);

  // Product search
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productSearching, setProductSearching] = useState(false);
  const productSearchRef = useRef(null);

  // Scanner & flow
  const [showScanner, setShowScanner] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [showNewProductModal, setShowNewProductModal] = useState(false);

  useEffect(() => {
    if (!isStaff) navigate('/connexion');
  }, [isStaff, navigate]);

  // --- Customer search (debounced) ---
  useEffect(() => {
    if (mode !== 'existing') return undefined;
    const handler = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/admin/pos/customers?q=${encodeURIComponent(customerSearch)}`
        );
        setCustomerResults(res.data);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.debug('[pos]', e);
      }
    }, 250);
    return () => clearTimeout(handler);
  }, [customerSearch, mode]);

  // --- Product search (debounced) ---
  useEffect(() => {
    const q = productQuery.trim();
    if (!q || q.length < 2) {
      setProductResults([]);
      return undefined;
    }
    setProductSearching(true);
    const handler = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/products?search=${encodeURIComponent(q)}&limit=8`
        );
        setProductResults(res.data || []);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.debug('[pos]', e);
      } finally {
        setProductSearching(false);
      }
    }, 250);
    return () => clearTimeout(handler);
  }, [productQuery]);

  // --- Barcode scan ---
  const handleBarcodeScan = useCallback(async (barcode) => {
    setShowScanner(false);
    try {
      const res = await axios.get(`${API_URL}/api/products/barcode/${barcode}`);
      addProductToCart(res.data);
    } catch (err) {
      toast.error(`Produit non trouvé (${barcode})`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cart items keep insertion order with the LAST added product at the TOP so
  // the admin can immediately fill its quantity without scrolling.

  // --- Cart ops ---
  const addProductToCart = useCallback((product) => {
    if (!product || !product.id) return;
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.product.id === product.id);
      if (idx >= 0) {
        const copy = [...prev];
        const next = Math.min(copy[idx].quantity + 1, product.stock || 9999);
        if (next === copy[idx].quantity) {
          toast.warning(`Stock max atteint pour ${product.name}`);
        }
        copy[idx] = { ...copy[idx], quantity: next };
        return copy;
      }
      if ((product.stock || 0) <= 0) {
        toast.error(`${product.name} en rupture de stock`);
        return prev;
      }
      return [{ product, quantity: 1, override: '' }, ...prev];
    });
    setProductQuery('');
    setProductResults([]);
    productSearchRef.current?.focus();
  }, []);

  const updateQty = (idx, qty) => {
    setItems((prev) => {
      // Clamp at 1 — removing an item should only happen via the trash button,
      // not by reaching 0 while editing the input.
      if (qty < 1) qty = 1;
      const max = prev[idx].product.stock || 9999;
      if (qty > max) {
        toast.error(`Stock insuffisant (${max} dispo)`);
        return prev;
      }
      const copy = [...prev];
      copy[idx] = { ...copy[idx], quantity: qty };
      return copy;
    });
  };

  // Lets the user clear the input mid-edit (e.g. delete "1" to type "12") —
  // empty / NaN keeps quantity as-is until they type a valid number.
  const onQtyInputChange = (idx, raw) => {
    if (raw === '') return; // ignore intermediate empty state
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1) return;
    updateQty(idx, n);
  };

  const setOverride = (idx, override) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], override };
      return copy;
    });
  };

  // Inline cost-price editing — used when admin wants VIP pricing on a product
  // that lacks a cost_price. Persists to DB, then refreshes the product locally.
  const setProductCostPrice = useCallback(async (productId, value) => {
    const cp = parseFloat(value);
    if (!Number.isFinite(cp) || cp <= 0) {
      toast.error("Prix d'achat invalide");
      return;
    }
    try {
      const res = await axios.put(`${API_URL}/api/products/${productId}`,
        { cost_price: cp });
      const updated = res.data;
      setItems((prev) => prev.map((it) => (
        it.product.id === productId
          ? { ...it, product: { ...it.product, cost_price: updated.cost_price, vip_margin: updated.vip_margin } }
          : it
      )));
      toast.success(`Prix d'achat enregistré (${cp.toFixed(2)}€)`);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Échec de l'enregistrement"));
    }
  }, []);

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  // Quick restock (+qty) without leaving the POS — updates cart & search stocks
  const restockProduct = useCallback(async (product, qty) => {
    try {
      const res = await axios.put(`${API_URL}/api/products/${product.id}/stock/add?qty=${qty}`);
      const newStock = res.data.stock;
      setItems((prev) => prev.map((it) => (
        it.product.id === product.id
          ? { ...it, product: { ...it.product, stock: newStock } }
          : it
      )));
      setProductResults((prev) => prev.map((p) => (
        p.id === product.id ? { ...p, stock: newStock } : p
      )));
      toast.success(`+${qty} en stock — ${product.name} : ${newStock} disponibles`);
      return true;
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec du réassort'));
      return false;
    }
  }, []);

  // --- Totals & per-line resolved pricing ---
  const totalQty = useMemo(() => items.reduce((s, it) => s + it.quantity, 0), [items]);
  const cartTier = useMemo(() => computeCartTier(totalQty), [totalQty]);
  // A registered customer with a forced tier overrides the auto-tier per line
  // (unless the admin already explicitly set a tier_override on the line).
  const customerForcedTier = (mode === 'existing' && selectedCustomer?.pricing_tier) || null;

  const resolvedLines = useMemo(() => items.map((it) => {
    const effectiveOverride = it.override || customerForcedTier || '';
    const r = computeLine(it.product, it.quantity, effectiveOverride, cartTier);
    // True when the admin wanted VIP but the product had no cost_price → fallback gros
    const vipFallback = effectiveOverride === 'vip' && r.tier !== 'vip';
    return { ...it, ...r, vipFallback };
  }), [items, cartTier, customerForcedTier]);

  const grandTotal = useMemo(
    () => Math.round(resolvedLines.reduce((s, l) => s + l.total, 0) * 100) / 100,
    [resolvedLines],
  );

  const hasCustomer = mode === 'existing'
    ? !!selectedCustomer
    : !!walkinName.trim();
  const canSubmit = hasCustomer && items.length > 0 && !submitting;

  // --- Submit ---
  const submit = async (paid) => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        user_id: mode === 'existing' ? selectedCustomer.id : null,
        customer_name: mode === 'existing' ? selectedCustomer.name : walkinName.trim(),
        customer_phone: mode === 'existing'
          ? (selectedCustomer.phone || '')
          : walkinPhone.trim(),
        items: items.map((it) => ({
          product_id: it.product.id,
          quantity: it.quantity,
          tier_override: it.override || null,
        })),
        paid,
      };
      const res = await axios.post(`${API_URL}/api/admin/pos/orders`, payload);
      setCreatedOrder(res.data);
      toast.success(paid ? 'Commande payée enregistrée' : 'Commande créée — à encaisser');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec de la création'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitQuote = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        user_id: mode === 'existing' ? selectedCustomer.id : null,
        customer_name: mode === 'existing' ? selectedCustomer.name : walkinName.trim(),
        customer_phone: mode === 'existing'
          ? (selectedCustomer.phone || '')
          : walkinPhone.trim(),
        items: items.map((it) => ({
          product_id: it.product.id,
          quantity: it.quantity,
          tier_override: it.override || null,
        })),
      };
      await axios.post(`${API_URL}/api/admin/quotes`, payload);
      toast.success('Devis enregistré en brouillon');
      navigate('/admin/devis');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec de la création du devis'));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadTicket = async () => {
    if (!createdOrder?.id) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/pos/orders/${createdOrder.id}/ticket`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Impossible de générer le ticket'));
    }
  };

  const resetFlow = () => {
    setCreatedOrder(null);
    setItems([]);
    setWalkinName('');
    setWalkinPhone('');
    setSelectedCustomer(null);
    setCustomerSearch('');
    setProductQuery('');
    setProductResults([]);
  };

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16" data-testid="admin-pos-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link to="/admin"
            className="text-zinc-500 hover:text-[color:var(--color-primary)] text-xs flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3" /> Tableau de bord
          </Link>
          <p className="text-[color:var(--color-primary)] text-xs uppercase tracking-[0.3em] mb-2">Comptoir POS</p>
          <h1 className="text-3xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Nouvelle commande au comptoir
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Vente en direct pour client de passage ou enregistré.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: customer + product picker */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer card */}
            <CustomerSection
              mode={mode} setMode={setMode}
              walkinName={walkinName} setWalkinName={setWalkinName}
              walkinPhone={walkinPhone} setWalkinPhone={setWalkinPhone}
              customerSearch={customerSearch} setCustomerSearch={setCustomerSearch}
              customerResults={customerResults}
              selectedCustomer={selectedCustomer} setSelectedCustomer={setSelectedCustomer}
              disabled={!!createdOrder}
            />

            {/* Product picker */}
            <div className="border border-white/10 bg-[color:var(--color-surface)] p-5"
                 data-testid="pos-product-picker">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em]">
                  Ajouter un produit
                </p>
                <button type="button"
                  onClick={() => setShowScanner(true)}
                  className="flex items-center gap-1 border border-[color:rgb(var(--primary-rgb)/0.40)] text-[color:var(--color-primary)] px-3 py-1.5 text-xs hover:bg-[color:var(--color-primary)] hover:text-black transition-colors"
                  data-testid="pos-open-scanner">
                  <Scan className="w-3.5 h-3.5" /> Scanner
                </button>
              </div>
              <div className="relative">
                <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  ref={productSearchRef}
                  type="text"
                  value={productQuery}
                  onChange={(e) => setProductQuery(e.target.value)}
                  placeholder="Nom du parfum, marque, code-barres…"
                  disabled={!!createdOrder}
                  data-testid="pos-product-search"
                  className="w-full bg-black border border-white/10 text-white pl-10 pr-3 py-2.5 text-sm focus:border-[color:var(--color-primary)] outline-none disabled:opacity-50"
                />
              </div>
              {productSearching && (
                <p className="text-zinc-500 text-xs mt-2">Recherche…</p>
              )}
              {productResults.length > 0 && (
                <ul className="mt-3 max-h-72 overflow-y-auto divide-y divide-white/5 border border-white/5">
                  {productResults.map((p) => (
                    <li key={p.id} className="flex items-center gap-2 pr-2">
                      <button type="button"
                        onClick={() => addProductToCart(p)}
                        disabled={(p.stock || 0) <= 0}
                        data-testid={`pos-add-product-${p.id}`}
                        className="flex-1 min-w-0 flex items-center justify-between gap-3 px-3 py-2 hover:bg-white/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-left">
                        <div className="flex items-center gap-3 min-w-0">
                          {p.image_url && (
                            <img src={p.image_url} alt=""
                              className="w-10 h-10 object-cover bg-white/5 flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-white text-sm truncate">{p.name}</p>
                            <p className="text-zinc-500 text-xs truncate">
                              {p.brand} · stock {p.stock} · {Number(p.price_gros || 0).toFixed(2)}€ gros
                            </p>
                          </div>
                        </div>
                        <Plus className="w-4 h-4 text-[color:var(--color-primary)] flex-shrink-0" />
                      </button>
                      <RestockControl product={p} onRestock={restockProduct} />
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={() => setShowNewProductModal(true)}
                data-testid="pos-new-product-btn"
                className="mt-3 w-full flex items-center justify-center gap-2 border border-dashed border-white/20 text-zinc-400 hover:border-[color:var(--color-primary)] hover:text-[color:var(--color-primary)] px-3 py-2 text-xs uppercase tracking-wider transition-colors"
              >
                <PackagePlus className="w-3.5 h-3.5" />
                Créer un nouveau produit
              </button>
            </div>

            {/* Cart lines */}
            <div className="border border-white/10 bg-[color:var(--color-surface)] p-5" data-testid="pos-cart">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em]">
                  Articles ({items.length})
                </p>
                <p className="text-zinc-500 text-xs">
                  Total quantité : <span className="text-white">{totalQty}</span> · Mode panier :
                  {' '}<span className="text-white capitalize">{cartTier}</span>
                </p>
              </div>
              {resolvedLines.length === 0 ? (
                <p className="text-zinc-600 text-sm italic py-6 text-center">
                  Aucun article. Scannez un code-barres ou recherchez ci-dessus.
                </p>
              ) : (
                <ul className="space-y-3">
                  {resolvedLines.map((it, idx) => (
                    <li key={it.product.id}
                        className="border border-white/5 bg-black p-3"
                        data-testid={`pos-cart-item-${it.product.id}`}>
                      <div className="flex gap-3">
                        {it.product.image_url && (
                          <img src={it.product.image_url} alt=""
                            className="w-14 h-14 object-cover bg-white/5 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-white text-sm truncate">{it.product.name}</p>
                              <p className="text-zinc-500 text-xs">
                                {it.product.brand} · stock {it.product.stock}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <RestockControl product={it.product} onRestock={restockProduct} />
                              <button type="button"
                                onClick={() => removeItem(idx)}
                                data-testid={`pos-remove-${it.product.id}`}
                                className="text-zinc-600 hover:text-red-400">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Quantity & tier picker */}
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            <div className="flex items-center border border-white/10">
                              <button type="button"
                                onClick={() => updateQty(idx, it.quantity - 1)}
                                className="p-1.5 text-zinc-400 hover:text-white"
                                data-testid={`pos-qty-minus-${it.product.id}`}>
                                <Minus className="w-3.5 h-3.5" />
                              </button>
                              <input type="number" min="1" value={it.quantity}
                                onChange={(e) => onQtyInputChange(idx, e.target.value)}
                                onBlur={(e) => {
                                  // Re-clamp on blur in case the input was emptied
                                  const n = parseInt(e.target.value, 10);
                                  if (!Number.isFinite(n) || n < 1) updateQty(idx, 1);
                                }}
                                className="w-12 bg-transparent text-white text-center text-sm py-1 outline-none"
                                data-testid={`pos-qty-${it.product.id}`} />
                              <button type="button"
                                onClick={() => updateQty(idx, it.quantity + 1)}
                                className="p-1.5 text-zinc-400 hover:text-white"
                                data-testid={`pos-qty-plus-${it.product.id}`}>
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                            </div>

                            <TierPicker
                              productId={it.product.id}
                              value={it.override}
                              activeTier={it.tier}
                              hasVip={Number(it.product.cost_price || 0) > 0}
                              onChange={(v) => setOverride(idx, v)}
                            />

                            <p className="ml-auto text-white text-sm">
                              {it.unit_price.toFixed(2)} € ·{' '}
                              <span className="text-[color:var(--color-primary)] font-medium">
                                {it.total.toFixed(2)} €
                              </span>
                            </p>
                          </div>

                          {/* VIP fallback notice + inline cost-price editor */}
                          {it.vipFallback && (
                            <VipCostPriceInline
                              productId={it.product.id}
                              vipMargin={it.product.vip_margin}
                              onSave={(v) => setProductCostPrice(it.product.id, v)}
                            />
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Right column: summary & actions */}
          <div className="lg:col-span-1">
            <div className="border border-[color:rgb(var(--primary-rgb)/0.30)] bg-[color:var(--color-surface)] p-5 sticky top-24"
                 data-testid="pos-summary">
              <p className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em] mb-3">
                Récapitulatif
              </p>

              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between text-zinc-400">
                  <span>Articles</span><span className="text-white">{items.length}</span>
                </div>
                <div className="flex justify-between text-zinc-400">
                  <span>Quantité totale</span><span className="text-white">{totalQty}</span>
                </div>
                <div className="border-t border-white/10 my-3" />
                <div className="flex justify-between items-baseline">
                  <span className="text-zinc-400">Total</span>
                  <span className="text-white text-3xl font-light"
                        style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {grandTotal.toFixed(2)} €
                  </span>
                </div>
              </div>

              {!hasCustomer && (
                <p className="text-orange-400 text-xs mb-3">
                  Renseignez le client avant de finaliser.
                </p>
              )}

              <div className="space-y-2">
                <button type="button"
                  onClick={() => submit(true)}
                  disabled={!canSubmit}
                  data-testid="pos-submit-paid"
                  className="w-full flex items-center justify-center gap-2 bg-[color:var(--color-primary)] text-black font-medium py-3 hover:bg-[color:var(--color-primary-hover)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm uppercase tracking-wider">
                  <Banknote className="w-4 h-4" />
                  Payé maintenant
                </button>
                <button type="button"
                  onClick={() => submit(false)}
                  disabled={!canSubmit}
                  data-testid="pos-submit-unpaid"
                  className="w-full flex items-center justify-center gap-2 border border-[color:rgb(var(--primary-rgb)/0.40)] text-[color:var(--color-primary)] py-3 hover:bg-[color:rgb(var(--primary-rgb)/0.10)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm uppercase tracking-wider">
                  <Hourglass className="w-4 h-4" />
                  À encaisser plus tard
                </button>
                <button type="button"
                  onClick={submitQuote}
                  disabled={!canSubmit}
                  data-testid="pos-submit-quote"
                  className="w-full flex items-center justify-center gap-2 border border-blue-500/40 text-blue-300 py-3 hover:bg-blue-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm uppercase tracking-wider">
                  <FileSignature className="w-4 h-4" />
                  Enregistrer comme devis
                </button>
              </div>

              <p className="text-zinc-600 text-[10px] mt-4 leading-relaxed">
                « Payé » crée une commande livrée et marque l'encaissement.
                <br />« À encaisser » crée une commande prête (statut <i>ready</i>).
                <br />« Devis » crée un brouillon sans toucher au stock (réservé à l'envoi).
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scanner modal */}
      {showScanner && (
        <BarcodeScanner
          onDetected={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Success modal */}
      {createdOrder && (
        <SuccessModal
          order={createdOrder}
          onPrint={downloadTicket}
          onNew={resetFlow}
          onClose={() => navigate('/admin/commandes')}
        />
      )}

      {showNewProductModal && (
        <NewProductQuickModal
          onClose={() => setShowNewProductModal(false)}
          onCreated={(prod) => {
            setShowNewProductModal(false);
            addProductToCart(prod);
          }}
        />
      )}
    </div>
  );
};

// =========================================================================

const VipCostPriceInline = ({ productId, vipMargin, onSave }) => {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const margin = vipMargin == null || vipMargin === '' ? 1 : Number(vipMargin) || 1;
  const preview = (() => {
    const cp = parseFloat(value);
    if (!Number.isFinite(cp) || cp <= 0) return null;
    return (cp + margin).toFixed(2);
  })();
  const submit = async () => {
    setSaving(true);
    try { await onSave(value); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-2 flex items-start gap-2 border border-amber-500/30 bg-amber-500/5 p-2"
         data-testid={`pos-vip-fallback-${productId}`}>
      <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-amber-300 text-[11px]">
          Prix VIP indisponible — coût d'achat manquant. Tarif Gros appliqué.
        </p>
        <p className="text-zinc-500 text-[10px] mt-0.5">
          💡 Définissez le prix d'achat pour activer le tarif VIP exceptionnellement.
        </p>
        <div className="flex items-center gap-2 mt-2">
          <input
            type="number"
            step="0.01"
            min="0"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Prix d'achat €"
            data-testid={`pos-vip-cost-input-${productId}`}
            className="bg-black border border-amber-500/40 text-white text-xs px-2 py-1 w-32 focus:border-amber-400 outline-none"
          />
          <button type="button"
            onClick={submit}
            disabled={saving || !preview}
            data-testid={`pos-vip-cost-save-${productId}`}
            className="text-xs uppercase tracking-wider bg-amber-500 text-black px-3 py-1 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? '…' : 'Activer VIP'}
          </button>
          {preview && (
            <span className="text-[10px] text-amber-300">
              → Prix VIP = {preview}€ (marge {margin.toFixed(2)}€)
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// =========================================================================

const CustomerSection = ({
  mode, setMode,
  walkinName, setWalkinName,
  walkinPhone, setWalkinPhone,
  customerSearch, setCustomerSearch,
  customerResults,
  selectedCustomer, setSelectedCustomer,
  disabled,
}) => (
  <div className="border border-white/10 bg-[color:var(--color-surface)] p-5" data-testid="pos-customer">
    <p className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em] mb-3">Client</p>

    <div className="grid grid-cols-2 gap-2 mb-4">
      <button type="button" onClick={() => setMode('walkin')} disabled={disabled}
        data-testid="pos-mode-walkin"
        className={`flex items-center justify-center gap-2 py-2.5 text-xs uppercase tracking-wider border transition-colors disabled:opacity-50 ${
          mode === 'walkin'
            ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-black'
            : 'border-white/10 text-zinc-400 hover:border-white/30'
        }`}>
        <UserPlus className="w-3.5 h-3.5" />
        De passage
      </button>
      <button type="button" onClick={() => setMode('existing')} disabled={disabled}
        data-testid="pos-mode-existing"
        className={`flex items-center justify-center gap-2 py-2.5 text-xs uppercase tracking-wider border transition-colors disabled:opacity-50 ${
          mode === 'existing'
            ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-black'
            : 'border-white/10 text-zinc-400 hover:border-white/30'
        }`}>
        <User className="w-3.5 h-3.5" />
        Client enregistré
      </button>
    </div>

    {mode === 'walkin' ? (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          type="text" placeholder="Nom du client *"
          value={walkinName}
          onChange={(e) => setWalkinName(e.target.value)}
          disabled={disabled}
          data-testid="pos-walkin-name"
          className="bg-black border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[color:var(--color-primary)] outline-none disabled:opacity-50"
        />
        <input
          type="tel" placeholder="Téléphone (optionnel)"
          value={walkinPhone}
          onChange={(e) => setWalkinPhone(e.target.value)}
          disabled={disabled}
          data-testid="pos-walkin-phone"
          className="bg-black border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[color:var(--color-primary)] outline-none disabled:opacity-50"
        />
      </div>
    ) : (
      <div>
        {selectedCustomer ? (
          <div className="flex items-center justify-between border border-[color:rgb(var(--primary-rgb)/0.30)] bg-[color:rgb(var(--primary-rgb)/0.05)] p-3">
            <div>
              <p className="text-white text-sm">{selectedCustomer.name}</p>
              <p className="text-zinc-500 text-xs">{selectedCustomer.phone}</p>
              {selectedCustomer.pricing_tier && (
                <p className="text-[10px] uppercase tracking-wider text-[color:var(--color-primary)] mt-1"
                   data-testid="pos-customer-tier-badge">
                  ✨ Tarif forcé : {selectedCustomer.pricing_tier === 'vip'
                    ? 'VIP'
                    : selectedCustomer.pricing_tier === 'gros'
                      ? 'Gros'
                      : 'Semi-gros'}
                </p>
              )}
            </div>
            <button type="button"
              onClick={() => setSelectedCustomer(null)}
              disabled={disabled}
              data-testid="pos-clear-customer"
              className="text-zinc-500 hover:text-red-400">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div>
            <div className="relative mb-2">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Nom ou téléphone…"
                disabled={disabled}
                data-testid="pos-customer-search"
                className="w-full bg-black border border-white/10 text-white pl-10 pr-3 py-2.5 text-sm focus:border-[color:var(--color-primary)] outline-none disabled:opacity-50"
              />
            </div>
            {customerResults.length > 0 && (
              <ul className="max-h-40 overflow-y-auto divide-y divide-white/5 border border-white/5">
                {customerResults.map((c) => (
                  <li key={c.id}>
                    <button type="button"
                      onClick={() => setSelectedCustomer(c)}
                      data-testid={`pos-pick-customer-${c.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-white/5">
                      <p className="text-white text-sm">{c.name}</p>
                      <p className="text-zinc-500 text-xs">{c.phone}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    )}
  </div>
);

// =========================================================================

const TierPicker = ({ productId, value, activeTier, hasVip, onChange }) => {
  const options = [
    { v: '', label: 'Auto' },
    { v: 'detail', label: 'Détail' },
    { v: 'semi_gros', label: 'S-Gros' },
    { v: 'gros', label: 'Gros' },
    { v: 'vip', label: 'VIP' },
  ];
  return (
    <div className="flex items-center gap-1">
      {options.map((opt) => {
        const isActive = (value || '') === opt.v;
        const tone = opt.v === 'vip'
          ? 'text-[color:var(--color-primary)] border-[color:rgb(var(--primary-rgb)/0.40)]'
          : 'text-zinc-400 border-white/10';
        const activeTone = opt.v === 'vip'
          ? 'bg-[color:var(--color-primary)] text-black border-[color:var(--color-primary)]'
          : 'bg-white text-black border-white';
        const disabled = opt.v === 'vip' && !hasVip;
        return (
          <button key={opt.v}
            type="button"
            onClick={() => onChange(opt.v)}
            disabled={disabled}
            title={disabled ? 'Prix d\'achat manquant pour ce produit' : ''}
            data-testid={`pos-tier-${productId}-${opt.v || 'auto'}`}
            className={`px-2 py-1 text-[10px] uppercase tracking-wider border transition-colors disabled:opacity-30 disabled:cursor-not-allowed
              ${isActive ? activeTone : tone}`}>
            {opt.v === 'vip' && <Crown className="w-3 h-3 inline mr-0.5" />}
            {opt.label}
          </button>
        );
      })}
      <span className={`ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${TIER_COLORS[activeTier] || ''}`}>
        {TIER_LABELS[activeTier] || activeTier}
      </span>
    </div>
  );
};

// =========================================================================

const SuccessModal = ({ order, onPrint, onNew, onClose }) => {
  const paid = order.status === 'delivered' || order.payment_status === 'paid';
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
         data-testid="pos-success-modal">
      <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.40)] max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="w-6 h-6 text-[color:var(--color-primary)]" />
          <h2 className="text-white text-xl font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Commande {paid ? 'payée' : 'enregistrée'}
          </h2>
        </div>
        <div className="space-y-1 text-sm mb-5">
          <p className="text-zinc-400">Référence :{' '}
            <span className="text-white font-mono">#{order.id.slice(-8).toUpperCase()}</span>
          </p>
          <p className="text-zinc-400">Client :{' '}
            <span className="text-white">{order.customer_name}</span>
          </p>
          <p className="text-zinc-400">Total :{' '}
            <span className="text-[color:var(--color-primary)] text-lg">{Number(order.total).toFixed(2)} €</span>
          </p>
          <p className="text-zinc-400">Statut :{' '}
            <span className={paid ? 'text-emerald-400' : 'text-orange-400'}>
              {paid ? 'Livrée — payée' : 'Prête — à encaisser'}
            </span>
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <button type="button" onClick={onPrint}
            data-testid="pos-print-ticket"
            className="flex items-center justify-center gap-2 border border-[color:rgb(var(--primary-rgb)/0.40)] text-[color:var(--color-primary)] py-2.5 hover:bg-[color:rgb(var(--primary-rgb)/0.10)] text-xs uppercase tracking-wider">
            <Printer className="w-3.5 h-3.5" />
            Imprimer ticket
          </button>
          <button type="button" onClick={onNew}
            data-testid="pos-new-order"
            className="flex items-center justify-center gap-2 bg-[color:var(--color-primary)] text-black py-2.5 hover:bg-[color:var(--color-primary-hover)] text-xs uppercase tracking-wider">
            <Plus className="w-3.5 h-3.5" />
            Nouvelle commande
          </button>
        </div>
        <button type="button" onClick={onClose}
          data-testid="pos-go-orders"
          className="w-full text-zinc-500 hover:text-white text-xs mt-2">
          Voir toutes les commandes →
        </button>
      </div>
    </div>
  );
};

// Inline quick-restock control: "+ Stock" → qty input → ✓ (PUT /stock/add)
const RestockControl = ({ product, onRestock }) => {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState('');

  const submit = async (e) => {
    e.stopPropagation();
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n < 1) {
      toast.error('Quantité invalide');
      return;
    }
    const ok = await onRestock(product, n);
    if (ok) { setOpen(false); setQty(''); }
  };

  if (!open) {
    return (
      <button type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title="Ajouter du stock (réassort rapide)"
        data-testid={`pos-restock-btn-${product.id}`}
        className="flex items-center gap-1 border border-emerald-500/40 text-emerald-300 px-2 py-1 text-[10px] uppercase tracking-wider hover:bg-emerald-500/10 transition-colors flex-shrink-0">
        <PackagePlus className="w-3 h-3" /> Stock
      </button>
    );
  }
  return (
    <span className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
      <input type="number" min="1" autoFocus value={qty}
        onChange={(e) => setQty(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(e); }}
        placeholder="+qté"
        data-testid={`pos-restock-qty-${product.id}`}
        className="w-16 bg-black border border-emerald-500/50 text-white px-2 py-1 text-xs outline-none" />
      <button type="button" onClick={submit}
        data-testid={`pos-restock-confirm-${product.id}`}
        className="bg-emerald-500 text-black p-1 hover:bg-emerald-400 transition-colors">
        <Check className="w-3.5 h-3.5" />
      </button>
      <button type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(false); setQty(''); }}
        data-testid={`pos-restock-cancel-${product.id}`}
        className="text-zinc-500 hover:text-white p-1">
        <X className="w-3.5 h-3.5" />
      </button>
    </span>
  );
};

export default AdminPreparation;

// =========================================================================
// Quick-create product modal — used when a walk-in needs a product that
// isn't in the catalog yet. Only required fields are captured (name, prices,
// stock); photos and descriptions can be filled later via Admin → Produits.

const NewProductQuickModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('Parfum');
  const [costPrice, setCostPrice] = useState('');
  const [priceDetail, setPriceDetail] = useState('');
  const [priceSemiGros, setPriceSemiGros] = useState('');
  const [priceGros, setPriceGros] = useState('');
  const [stock, setStock] = useState('1');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const nm = name.trim();
    if (!nm) { toast.error('Nom du produit requis'); return; }
    const pd = parseFloat(priceDetail);
    const psg = parseFloat(priceSemiGros);
    const pg = parseFloat(priceGros);
    const st = parseInt(stock, 10);
    if (!Number.isFinite(pd) || pd <= 0 ||
        !Number.isFinite(psg) || psg <= 0 ||
        !Number.isFinite(pg) || pg <= 0) {
      toast.error('Les 3 prix de vente sont obligatoires (> 0)');
      return;
    }
    if (!(pd >= psg && psg >= pg)) {
      toast.error('Ordre incohérent : détail ≥ semi-gros ≥ gros');
      return;
    }
    if (!Number.isFinite(st) || st < 0) {
      toast.error('Stock invalide');
      return;
    }
    const cp = parseFloat(costPrice);
    const payload = {
      name: nm,
      brand: brand.trim() || 'À renseigner',
      category: category.trim() || 'Parfum',
      description: '',
      image_url: '',
      price_detail: pd,
      price_semi_gros: psg,
      price_gros: pg,
      stock: st,
      is_available: true,
    };
    if (Number.isFinite(cp) && cp > 0) payload.cost_price = cp;

    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/products`, payload);
      toast.success(`${nm} créé et ajouté à la commande`);
      onCreated(res.data);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec de la création'));
    } finally {
      setSaving(false);
    }
  };

  const fld = "w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none";
  const lbl = "text-zinc-500 text-[10px] uppercase tracking-wider block mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
         data-testid="pos-new-product-modal">
      <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.30)] max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Nouveau produit (rapide)
          </h2>
          <button type="button" onClick={onClose}
            data-testid="pos-new-product-close"
            className="text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-zinc-500 text-xs mb-4">
          Création minimale : nom + 3 prix + stock. Vous pourrez compléter photos, EAN et description plus tard depuis <span className="text-[color:var(--color-primary)]">Admin → Produits</span>.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className={lbl}>Nom du produit *</label>
            <input type="text" value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="pos-new-name" className={fld} autoFocus />
          </div>
          <div>
            <label className={lbl}>Marque</label>
            <input type="text" value={brand}
              onChange={(e) => setBrand(e.target.value)}
              placeholder="(facultatif)"
              data-testid="pos-new-brand" className={fld} />
          </div>
          <div>
            <label className={lbl}>Catégorie</label>
            <input type="text" value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="pos-new-category" className={fld} />
          </div>
          <div className="sm:col-span-2 border-t border-white/5 pt-3 mt-1">
            <p className="text-zinc-500 text-[10px] uppercase tracking-widest mb-2">Tarifs (€)</p>
          </div>
          <div>
            <label className={lbl}>Prix d'achat</label>
            <input type="number" step="0.01" min="0" value={costPrice}
              onChange={(e) => setCostPrice(e.target.value)}
              placeholder="(facultatif)"
              data-testid="pos-new-cost" className={fld} />
          </div>
          <div>
            <label className={lbl}>Stock initial *</label>
            <input type="number" min="0" value={stock}
              onChange={(e) => setStock(e.target.value)}
              data-testid="pos-new-stock" className={fld} />
          </div>
          <div>
            <label className={lbl}>Prix détail *</label>
            <input type="number" step="0.01" min="0" value={priceDetail}
              onChange={(e) => setPriceDetail(e.target.value)}
              data-testid="pos-new-price-detail" className={fld} />
          </div>
          <div>
            <label className={lbl}>Prix semi-gros *</label>
            <input type="number" step="0.01" min="0" value={priceSemiGros}
              onChange={(e) => setPriceSemiGros(e.target.value)}
              data-testid="pos-new-price-semi" className={fld} />
          </div>
          <div className="sm:col-span-2">
            <label className={lbl}>Prix gros *</label>
            <input type="number" step="0.01" min="0" value={priceGros}
              onChange={(e) => setPriceGros(e.target.value)}
              data-testid="pos-new-price-gros" className={fld} />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button type="button" onClick={submit}
            disabled={saving}
            data-testid="pos-new-submit"
            className="flex-1 bg-[color:var(--color-primary)] text-black py-2.5 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40">
            {saving ? '…' : 'Créer et ajouter au panier'}
          </button>
          <button type="button" onClick={onClose}
            className="border border-white/20 text-zinc-400 px-5 py-2.5 text-xs uppercase tracking-wider hover:border-white/40">
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};
